/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useCallback } from 'react';

import { useCommandIntelligence } from '../../CommandIntelligenceContext';
import { DEFAULT_MAX_ATTACHED_ITEMS } from '../../constants';
import { defaultNormalizeContextItem } from '../utils/defaultNormalizeContextItem';
import { defaultNormalizeContextItems } from '../utils/defaultNormalizeContextItems';

export const useCommandIntelligenceScreenActions = ({
  maxAttachedItems = DEFAULT_MAX_ATTACHED_ITEMS,
  normalizeContextItem = defaultNormalizeContextItem,
  normalizeContextItems = defaultNormalizeContextItems,
  onListTruncated,
} = {}) => {
  const { openForItemList } = useCommandIntelligence();

  const openList = useCallback(
    ({ items, listFilters, availableItems, maxAttachedItems: maxItems = maxAttachedItems }) => {
      const normalizedItems = normalizeContextItems(items);

      if (!normalizedItems.length) {
        return;
      }

      const cappedItems = normalizedItems.slice(0, maxItems);

      if (normalizedItems.length > maxItems) {
        onListTruncated?.({ requested: normalizedItems.length, max: maxItems });
      }

      openForItemList?.({
        itemList: cappedItems,
        listFilters,
        availableItems: availableItems ?? normalizedItems,
      });
    },
    [maxAttachedItems, normalizeContextItems, onListTruncated, openForItemList]
  );

  const openItem = useCallback(
    ({ itemRef, typeId, snapshot, label, crn, id, listFilters, ...rest }) => {
      const contextItem = normalizeContextItem({ itemRef, typeId, snapshot, label, crn, id, ...rest });

      if (!contextItem) {
        return;
      }

      openForItemList?.({
        itemList: [contextItem],
        listFilters,
        availableItems: [contextItem],
      });
    },
    [normalizeContextItem, openForItemList]
  );

  return { openList, openItem };
};
